import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import * as Dialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { X, ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/lib/components/ui/button";
import { Badge } from "@/lib/components/ui/badge";
import { Input } from "@/lib/components/ui/input";
import { Skeleton } from "@/lib/components/ui/skeleton";
import { fetchProduct, addProductToCart } from "@/lib/catalogApi";
import { useCart } from "@/features/cart/hooks/useCart";
import { formatCurrency } from "@/lib/format";
import type { Product } from "@/types/catalog";

function ProductDetailSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
      {/* Images column */}
      <div className="space-y-3">
        <Skeleton className="w-full aspect-square rounded-lg" />
        <div className="grid grid-cols-4 gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded" />
          ))}
        </div>
      </div>

      {/* Details column */}
      <div className="space-y-5">
        <div className="space-y-2">
          <Skeleton className="h-8 w-3/4" />
          <div className="flex gap-2 mt-2">
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
        <Skeleton className="h-10 w-1/3" />
        <div className="flex gap-3">
          <Skeleton className="h-10 w-20" />
          <Skeleton className="h-10 flex-1" />
        </div>
        <Skeleton className="h-3 w-32" />
      </div>
    </div>
  );
}

export default function ProductDetailPage() {
  const { t } = useTranslation("catalog");
  const { slug } = useParams<{ slug: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [lbOpen, setLbOpen] = useState(false);
  const [lbIndex, setLbIndex] = useState(0);
  const { increment } = useCart();

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetchProduct(slug)
      .then(setProduct)
      .catch(() => toast.error(t("products.failedToLoadDetail")))
      .finally(() => setLoading(false));
  }, [slug]);

  const images = product?.images ?? [];

  const goto = useCallback(
    (dir: -1 | 1) =>
      setLbIndex(i => (i + dir + images.length) % images.length),
    [images.length],
  );

  useEffect(() => {
    if (!lbOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") { e.preventDefault(); goto(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); goto(-1); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lbOpen, goto]);

  const handleAddToCart = async () => {
    if (!product) return;
    try {
      await addProductToCart(product.id, quantity);
      increment(quantity);
      toast.success(t("products.addedToCart", { name: product.name }));
    } catch {
      toast.error(t("products.failedToAdd"));
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-muted text-foreground">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <Link
          to="/products"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("products.backToProducts")}
        </Link>

        {loading ? (
          <ProductDetailSkeleton />
        ) : !product ? (
          <p className="text-muted-foreground py-12 text-center">{t("products.notFound")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Images */}
            <div className="space-y-3">
              {images.length > 0 ? (
                <>
                  <button
                    type="button"
                    onClick={() => { setLbIndex(0); setLbOpen(true); }}
                    className="block w-full rounded-lg overflow-hidden border border-border bg-card"
                    aria-label="View image gallery"
                  >
                    <img
                      src={images[0].url}
                      alt={images[0].alt ?? product.name}
                      className="w-full aspect-square object-cover"
                    />
                  </button>
                  {images.length > 1 && (
                    <div className="grid grid-cols-4 gap-2">
                      {images.slice(1, 5).map((img, idx) => (
                        <button
                          key={img.id}
                          type="button"
                          onClick={() => { setLbIndex(idx + 1); setLbOpen(true); }}
                          className="rounded overflow-hidden border border-border bg-card"
                          aria-label={`View image ${idx + 2}`}
                        >
                          <img
                            src={img.url}
                            alt={img.alt ?? product.name}
                            className="w-full aspect-square object-cover"
                            loading="lazy"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full aspect-square rounded-lg border border-border bg-card flex items-center justify-center text-muted-foreground">
                  {t("products.noImages")}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="space-y-5">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">{product.name}</h1>
                {product.categories.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {product.categories.map(cat => (
                      <Badge key={cat.id} variant="outline">{cat.name}</Badge>
                    ))}
                  </div>
                )}
              </div>

              {product.description && (
                <p className="text-muted-foreground leading-relaxed">{product.description}</p>
              )}

              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-extrabold">
                  {formatCurrency(product.gross_price)}
                </span>
                <span className="text-sm text-muted-foreground">{t("products.inclVat")}</span>
              </div>

              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20 text-center"
                />
                <Button size="lg" className="flex-1" onClick={handleAddToCart}>
                  {t("products.addToCart")}
                </Button>
              </div>

              {product.product_code && (
                <p className="text-xs text-muted-foreground">{t("products.productCode")}: #{product.product_code}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox — only mounted when product is loaded */}
      {product && (
        <Dialog.Root open={lbOpen} onOpenChange={o => !o && setLbOpen(false)}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
          </Dialog.Portal>
          <Dialog.Portal>
            <Dialog.Content
              className="fixed inset-0 flex flex-col"
              aria-labelledby="product-lb-title"
              aria-describedby="product-lb-desc"
            >
              <VisuallyHidden>
                <Dialog.Title id="product-lb-title">{product.name} — Images</Dialog.Title>
                <Dialog.Description id="product-lb-desc">
                  Image {lbIndex + 1} of {images.length}
                </Dialog.Description>
              </VisuallyHidden>

              {/* Top bar */}
              <div className="pointer-events-none absolute left-0 right-0 top-16 p-4 flex items-center justify-between text-white">
                <div className="pointer-events-auto bg-black/40 rounded-full px-3 py-1 text-xs md:text-sm">
                  {product.name}
                </div>
                <div className="flex items-center gap-2">
                  <div className="pointer-events-auto bg-black/40 rounded-full px-3 py-1 text-xs md:text-sm">
                    {String(lbIndex + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
                  </div>
                  <Dialog.Close asChild>
                    <button
                      aria-label="Close gallery"
                      className="pointer-events-auto inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/40 hover:bg-black/60"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </Dialog.Close>
                </div>
              </div>

              {/* Image area */}
              <div className="flex-1 flex items-center justify-center px-4">
                {images[lbIndex] && (
                  <figure className="max-w-[min(92vw,1400px)] max-h-[82vh] flex flex-col items-center">
                    <img
                      key={lbIndex}
                      src={images[lbIndex].url}
                      alt={images[lbIndex].alt ?? product.name}
                      className="max-w-full max-h-[72vh] object-contain select-none"
                      draggable={false}
                      decoding="async"
                    />
                    {images[lbIndex].caption && (
                      <figcaption className="mt-3 text-center text-xs md:text-sm text-white/90">
                        {images[lbIndex].caption}
                      </figcaption>
                    )}
                  </figure>
                )}
              </div>

              {/* Nav buttons */}
              {images.length > 1 && (
                <>
                  <button
                    aria-label="Previous image"
                    onClick={() => goto(-1)}
                    className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white"
                  >
                    <ChevronLeft className="h-6 w-6 md:h-7 md:w-7" />
                  </button>
                  <button
                    aria-label="Next image"
                    onClick={() => goto(1)}
                    className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white"
                  >
                    <ChevronRight className="h-6 w-6 md:h-7 md:w-7" />
                  </button>
                </>
              )}
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </div>
  );
}
